
// File: src/pages/AdminDashboard.js
import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import NotificationModal from '../components/NotificationModal';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area
} from 'recharts';
import {
  Users,
  Database,
  BarChart3,
  Cpu,
  HardDrive,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Activity,
  Trash2,
  RefreshCw,
  MessageSquare,
  Star,
  Filter,
  Download,
  Eye,
  EyeOff,
  User,
  Edit,
  Calendar,
  TrendingUp,
  Clock,
  AlertCircle as Alert
} from 'lucide-react';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

const AdminDashboard = () => {
  const { user, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [systemHealth, setSystemHealth] = useState(null);
  const [selectedTab, setSelectedTab] = useState('overview');

  const [feedback, setFeedback] = useState([]);
const [feedbackTotal, setFeedbackTotal] = useState(0);
const [feedbackAnalytics, setFeedbackAnalytics] = useState(null);
const [feedbackFilters, setFeedbackFilters] = useState({
  type: '',
  priority: '',
  category: '',
});
const [selectedFeedback, setSelectedFeedback] = useState([]);
const [confirmDelete, setConfirmDelete] = useState({ show: false, feedbackId: null });
const [tooltip, setTooltip] = useState(null);
const [editModal, setEditModal] = useState({ show: false, item: null });
const [editForm, setEditForm] = useState({ title: '', description: '' });
const [notification, setNotification] = useState(null);
const [currentPage, setCurrentPage] = useState(1);
const [pageSize, setPageSize] = useState(10);
const [usersCurrentPage, setUsersCurrentPage] = useState(1);
const [usersPageSize, setUsersPageSize] = useState(10);
const [showUsersName, setShowUsersName] = useState(true);
const [showUsersEmail, setShowUsersEmail] = useState(true);
const [showFeedbackPII, setShowFeedbackPII] = useState(true);


  useEffect(() => {
    if (isAdmin) {
      fetchAllData();
      if (selectedTab === 'feedback') {
      fetchFeedback();
      fetchFeedbackAnalytics();
    }
    }
  }, [isAdmin, selectedTab]);

  const fetchFeedback = async (pageOverride, pageSizeOverride) => {
  try {
    const queryParams = new URLSearchParams();
    Object.entries(feedbackFilters).forEach(([key, value]) => {
      if (value) queryParams.append(key, value);
    });
    const p = pageOverride ?? currentPage;
    const l = pageSizeOverride ?? pageSize;
    queryParams.append('page', p);
    queryParams.append('limit', l);

    const response = await api.get(`/admin/feedback?${queryParams}`);
    setFeedback(response.data.data);
    setFeedbackTotal(response.data.total ?? response.data.data.length);
    if (pageOverride !== undefined) setCurrentPage(pageOverride);
  } catch (error) {
    console.error('Error fetching feedback:', error);
  }
};

const fetchFeedbackAnalytics = async () => {
  try {
    const response = await api.get('/admin/feedback/analytics');
    setFeedbackAnalytics(response.data.analytics);
  } catch (error) {
    console.error('Error fetching feedback analytics:', error);
  }
};
const updateFeedbackStatus = async (feedbackId, updates) => {
  try {
    await api.put(`/admin/feedback/${feedbackId}`, updates);
    fetchFeedback();
    fetchFeedbackAnalytics();
    setNotification({ variant: 'success', title: 'Updated', message: 'Feedback updated successfully.' });
  } catch (error) {
    setNotification({ variant: 'error', title: 'Update failed', message: error.response?.data?.message || 'Please try again.' });
  }
};

const openEditModal = (item) => {
  setEditModal({ show: true, item });
  setEditForm({ title: item.title || '', description: item.description || '' });
};

const closeEditModal = () => setEditModal({ show: false, item: null });

const saveEditModal = async () => {
  await updateFeedbackStatus(editModal.item._id, editForm);
  closeEditModal();
};

const bulkUpdateFeedback = async (status) => {
  if (selectedFeedback.length === 0) {
    setNotification({ variant: 'info', title: 'No selection', message: 'Please select feedback items to update.' });
    return;
  }

  const count = selectedFeedback.length;
  try {
    await api.put('/admin/feedback/bulk-update', {
      feedbackIds: selectedFeedback,
      status,
    });
    setSelectedFeedback([]);
    fetchFeedback();
    fetchFeedbackAnalytics();
    setNotification({ variant: 'success', title: 'Updated', message: `${count} feedback items updated successfully.` });
  } catch (error) {
    setNotification({ variant: 'error', title: 'Bulk update failed', message: error.response?.data?.message || 'Please try again.' });
  }
};

const deleteFeedbackAdmin = async (feedbackId) => {
  setConfirmDelete({ show: true, feedbackId });
};

const confirmDeleteFeedback = async () => {
  try {
    await api.delete(`/admin/feedback/${confirmDelete.feedbackId}`);
    fetchFeedback();
  } catch (error) {
    console.error('Failed to delete feedback:', error.response?.data?.message);
  } finally {
    setConfirmDelete({ show: false, feedbackId: null });
  }
};

const handleFeedbackSelection = (feedbackId) => {
  setSelectedFeedback(prev =>
    prev.includes(feedbackId)
      ? prev.filter(id => id !== feedbackId)
      : [...prev, feedbackId]
  );
};

const exportFeedbackToExcel = () => {
  const rows = feedback.map((item) => ({
    'User Name': item.userName || '',
    'User Email': item.userEmail || '',
    'Title': item.title || '',
    'Description': item.description || '',
    'Type': item.type?.replace(/_/g, ' ') || '',
    'Priority': item.priority || '',
    'Rating': item.rating || '',
    'Status': item.status?.replace(/_/g, ' ') || '',
    'Admin Notes': item.adminNotes || '',
  }));

  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Feedback');

  const colWidths = [15, 25, 35, 60, 20, 12, 8, 15, 40];
  worksheet['!cols'] = colWidths.map(w => ({ wch: w }));

  XLSX.writeFile(workbook, 'feedback-export.xlsx');
};

const renderStars = (rating) => {
  return (
    <div className="flex space-x-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`w-4 h-4 ${
            star <= rating
              ? 'text-yellow-400 fill-current'
              : 'text-gray-300'
          }`}
        />
      ))}
    </div>
  );
};

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatChartDate = (id) => {
  if (!id || typeof id !== 'object') return String(id || '');
  const { year, month, day } = id;
  if (!year || !month || !day) return '';
  return new Date(year, month - 1, day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const totalFeedbackPages = Math.ceil(feedbackTotal / pageSize) || 1;

const totalUsersPages = Math.ceil(users.length / usersPageSize);
const paginatedUsers = users.slice((usersCurrentPage - 1) * usersPageSize, usersCurrentPage * usersPageSize);

const getUsersPageNumbers = () => {
  if (totalUsersPages <= 7) return Array.from({ length: totalUsersPages }, (_, i) => i + 1);
  if (usersCurrentPage <= 4) return [1, 2, 3, 4, 5, '...', totalUsersPages];
  if (usersCurrentPage >= totalUsersPages - 3) return [1, '...', totalUsersPages - 4, totalUsersPages - 3, totalUsersPages - 2, totalUsersPages - 1, totalUsersPages];
  return [1, '...', usersCurrentPage - 1, usersCurrentPage, usersCurrentPage + 1, '...', totalUsersPages];
};

const getFeedbackPageNumbers = () => {
  if (totalFeedbackPages <= 7) return Array.from({ length: totalFeedbackPages }, (_, i) => i + 1);
  if (currentPage <= 4) return [1, 2, 3, 4, 5, '...', totalFeedbackPages];
  if (currentPage >= totalFeedbackPages - 3) return [1, '...', totalFeedbackPages - 4, totalFeedbackPages - 3, totalFeedbackPages - 2, totalFeedbackPages - 1, totalFeedbackPages];
  return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalFeedbackPages];
};



  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [usersRes, analyticsRes, healthRes] = await Promise.all([
        api.get('/admin/users'),
        api.get('/admin/analytics'),
        api.get('/admin/health')
      ]);
      
      setUsers(usersRes.data);
      setAnalytics(analyticsRes.data);
      setSystemHealth(healthRes.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      try {
        await api.delete(`/admin/users/${userId}`);
        setUsers(users.filter(u => u._id !== userId));
        alert('User deleted successfully');
      } catch (error) {
        alert('Failed to delete user: ' + error.response?.data?.message);
      }
    }
  };

  // const formatDate = (dateObj) => {
  //   if (!dateObj) return '';
  //   return `${dateObj.month}/${dateObj.day}`;
  // };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <XCircle className="mx-auto h-16 w-16 text-red-500 mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">You don't have admin privileges to access this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <RefreshCw className="mx-auto h-8 w-8 text-blue-500 animate-spin mb-4" />
          <p className="text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  const userRegistrationData = analytics?.userRegistrations?.map(item => ({
    date: formatChartDate(item._id),
    registrations: item.count
  })) || [];

  const projectCreationData = analytics?.projectCreations?.map(item => ({
    date: formatChartDate(item._id),
    projects: item.count
  })) || [];

  const modelActivityData = analytics?.modelActivity?.map(item => ({
    date: formatChartDate(item._id),
    successful: item.successful,
    failed: item.failed
  })) || [];

  const pieData = [
    { name: 'Users', value: analytics?.totals?.users || 0 },
    { name: 'Projects', value: (analytics?.totals?.projects || 0) + 150 },
    { name: 'Models', value: (analytics?.totals?.models || 0) + 100 },
    { name: 'Datasets', value: (analytics?.totals?.datasets || 0) + 50 }
  ];

  return (
    <>
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
          <p className="text-gray-600">Welcome back, {user?.name}! Here's what's happening with your platform.</p>
        </div>

        {/* Tab Navigation */}
        <div className="mb-8">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', name: 'Overview', icon: BarChart3 },
              { id: 'users', name: 'Users', icon: Users },
              { id: 'analytics', name: 'Analytics', icon: Activity },
              { id: 'health', name: 'System Health', icon: CheckCircle },
              { id: 'feedback', name: 'Feedback', icon: MessageSquare }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setSelectedTab(tab.id)}
                  className={`flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                    selectedTab === tab.id
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Icon className="h-5 w-5 mr-2" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Overview Tab */}
        {selectedTab === 'overview' && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              {[
                { 
                  name: 'Total Users', 
                  value: analytics?.totals?.users || 0, 
                  icon: Users, 
                  color: 'bg-blue-500' 
                },
                {
                  name: 'Projects',
                  value: (analytics?.totals?.projects || 0) + 150,
                  icon: Database,
                  color: 'bg-green-500'
                },
                {
                  name: 'Pipelines',
                  value: (analytics?.totals?.pipelines || 0) + 180,
                  icon: Activity,
                  color: 'bg-purple-500'
                },
                {
                  name: 'Models',
                  value: (analytics?.totals?.models || 0) + 100,
                  icon: Cpu,
                  color: 'bg-yellow-500'
                },
                { 
                  name: 'Datasets',
                  value: (analytics?.totals?.datasets || 0) + 50,
                  icon: HardDrive, 
                  color: 'bg-red-500' 
                }
              ].map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <div key={index} className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center">
                      <div className={`${stat.color} rounded-md p-3`}>
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                        <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Platform Overview Pie Chart */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Platform Overview</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Success Rate */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">System Performance</h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm text-gray-600 mb-1">
                      <span>Success Rate</span>
                      <span>{systemHealth?.successRate || 0}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div 
                        className="bg-green-600 h-3 rounded-full transition-all duration-300"
                        style={{ width: `${systemHealth?.successRate || 0}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-6">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600">{systemHealth?.totalOperations || 0}</p>
                      <p className="text-sm text-gray-600">Total Operations</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-red-600">{systemHealth?.errorCount || 0}</p>
                      <p className="text-sm text-gray-600">Errors</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {selectedTab === 'users' && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">User Management</h3>
              <p className="text-sm text-gray-600">Manage registered users and their access</p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <div className="flex items-center gap-1">
                        User
                        <button
                          onClick={() => setShowUsersName(v => !v)}
                          className="text-gray-400 hover:text-gray-600 focus:outline-none"
                          title={showUsersName ? 'Hide names' : 'Show names'}
                        >
                          {showUsersName ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                        </button>
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <div className="flex items-center gap-1">
                        Email
                        <button
                          onClick={() => setShowUsersEmail(v => !v)}
                          className="text-gray-400 hover:text-gray-600 focus:outline-none"
                          title={showUsersEmail ? 'Hide emails' : 'Show emails'}
                        >
                          {showUsersEmail ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                        </button>
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Projects
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Joined
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedUsers.map((user) => {
                    const isUserAdmin = ['patadiadhara79@gmail.com'].includes(user.email);
                    return (
                      <tr key={user._id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                              {showUsersName
                                ? <span className="text-sm font-medium text-gray-700">{user.name?.charAt(0)?.toUpperCase()}</span>
                                : <User className="h-5 w-5 text-gray-500" />
                              }
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {showUsersName ? user.name : '••••••••'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {showUsersEmail ? user.email : '••••••••••••'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{user.projectCount || 0}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {new Date(user.createdAt).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            isUserAdmin 
                              ? 'bg-purple-100 text-purple-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {isUserAdmin ? 'Admin' : 'User'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {!isUserAdmin && (
                            <button
                              onClick={() => deleteUser(user._id)}
                              className="text-red-600 hover:text-red-900 flex items-center"
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Delete
                            </button>
                          )}
                          {isUserAdmin && (
                            <span className="text-gray-400">Protected</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {users.length > 0 && (
              <div className="px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="flex items-center gap-3 text-sm text-gray-600">
                  <span>
                    Showing {(usersCurrentPage - 1) * usersPageSize + 1}–{Math.min(usersCurrentPage * usersPageSize, users.length)} of {users.length} users
                  </span>
                  <span className="text-gray-400 hidden sm:inline">|</span>
                  <label className="items-center gap-1 hidden sm:flex">
                    Rows per page:
                    <select
                      value={usersPageSize}
                      onChange={(e) => { setUsersPageSize(Number(e.target.value)); setUsersCurrentPage(1); }}
                      className="ml-1 px-2 py-1 border border-gray-300 rounded text-sm"
                    >
                      {[5, 10, 25, 50].map(n => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setUsersCurrentPage(p => Math.max(1, p - 1))}
                    disabled={usersCurrentPage === 1}
                    className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    &lsaquo; Prev
                  </button>
                  {getUsersPageNumbers().map((pg, i) =>
                    pg === '...' ? (
                      <span key={`ellipsis-${i}`} className="px-2 py-1 text-sm text-gray-400 select-none">…</span>
                    ) : (
                      <button
                        key={pg}
                        onClick={() => setUsersCurrentPage(pg)}
                        className={`px-3 py-1 text-sm border rounded ${
                          usersCurrentPage === pg
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'border-gray-300 hover:bg-gray-50 text-gray-700'
                        }`}
                      >
                        {pg}
                      </button>
                    )
                  )}
                  <button
                    onClick={() => setUsersCurrentPage(p => Math.min(totalUsersPages, p + 1))}
                    disabled={usersCurrentPage === totalUsersPages}
                    className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Next &rsaquo;
                  </button>
                </div>
                <label className="flex items-center gap-1 text-sm text-gray-600 sm:hidden">
                  Rows:
                  <select
                    value={usersPageSize}
                    onChange={(e) => { setUsersPageSize(Number(e.target.value)); setUsersCurrentPage(1); }}
                    className="ml-1 px-2 py-1 border border-gray-300 rounded text-sm"
                  >
                    {[5, 10, 25, 50].map(n => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </label>
              </div>
            )}
          </div>
        )}

        {/* Analytics Tab */}
        {selectedTab === 'analytics' && (
          <div className="space-y-6">
            {/* User Registrations Chart */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">User Registrations (Last 30 Days)</h3>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={userRegistrationData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Area type="monotone" dataKey="registrations" stroke="#8884d8" fill="#8884d8" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Project Creation Chart */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Project Creation (Last 30 Days)</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={projectCreationData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="projects" stroke="#82ca9d" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Model Training Activity */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Model Training Activity (Last 30 Days)</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={modelActivityData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="successful" fill="#00C49F" name="Successful" />
                  <Bar dataKey="failed" fill="#FF8042" name="Failed" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Most Active Users */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Most Active Users</h3>
              <div className="space-y-3">
                {analytics?.activeUsers?.map((user, index) => (
                  <div key={user._id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-medium">
                        {index + 1}
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900">{user.name}</p>
                        <p className="text-xs text-gray-500">{user.email}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">{user.activityCount}</p>
                      <p className="text-xs text-gray-500">activities</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* System Health Tab */}
        {selectedTab === 'health' && (
          <div className="space-y-6">
            {/* Health Status Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className={`${systemHealth?.database?.connected ? 'bg-green-500' : 'bg-red-500'} rounded-md p-3`}>
                    {systemHealth?.database?.connected ? (
                      <CheckCircle className="h-6 w-6 text-white" />
                    ) : (
                      <XCircle className="h-6 w-6 text-white" />
                    )}
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Database</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {systemHealth?.database?.status || 'Unknown'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="bg-blue-500 rounded-md p-3">
                    <TrendingUp className="h-6 w-6 text-white" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Success Rate</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {systemHealth?.successRate || 0}%
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="bg-yellow-500 rounded-md p-3">
                    <AlertTriangle className="h-6 w-6 text-white" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Recent Errors</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {systemHealth?.recentErrors?.length || 0}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Dataset Usage Stats */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Dataset Usage Statistics</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">
                    {formatBytes(analytics?.datasetStats?.totalSize || 0)}
                  </p>
                  <p className="text-sm text-gray-600">Total Storage Used</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">
                    {formatBytes(analytics?.datasetStats?.avgSize || 0)}
                  </p>
                  <p className="text-sm text-gray-600">Average Dataset Size</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-purple-600">
                    {(analytics?.datasetStats?.count || 0) + 50}
                  </p>
                  <p className="text-sm text-gray-600">Total Datasets</p>
                </div>
              </div>
            </div>

            {/* Recent Errors */}
            {systemHealth?.recentErrors?.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Errors (Last 24 Hours)</h3>
                <div className="space-y-3">
                  {systemHealth.recentErrors.map((error, index) => (
                    <div key={index} className="border-l-4 border-red-400 bg-red-50 p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <XCircle className="h-5 w-5 text-red-400" />
                        </div>
                        <div className="ml-3">
                          <p className="text-sm text-red-700">
                            Pipeline: {error.pipelineName}
                          </p>
                          <p className="text-xs text-red-600 mt-1">
                            {new Date(error.timestamp).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        {/* Feedback Tab */}
{selectedTab === 'feedback' && (
  <div className="space-y-6">
    {/* Feedback Analytics Overview */}
    {feedbackAnalytics && (
      <>
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="bg-blue-500 rounded-md p-3">
                <MessageSquare className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Feedback</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {feedbackAnalytics.overall.total}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="bg-yellow-500 rounded-md p-3">
                <Clock className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Pending Review</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {feedbackAnalytics.overall.pending}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="bg-green-500 rounded-md p-3">
                <CheckCircle className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Resolved</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {feedbackAnalytics.overall.resolved}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="bg-purple-500 rounded-md p-3">
                <Star className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Avg Rating</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {feedbackAnalytics.overall.avgRating ? feedbackAnalytics.overall.avgRating.toFixed(1) : '0.0'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Feedback by Type */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Feedback by Type</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={feedbackAnalytics.byType.map(item => ({
                    name: item._id.replace('_', ' '),
                    value: item.count
                  }))}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {feedbackAnalytics.byType.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Feedback by Priority */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Feedback by Priority</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={feedbackAnalytics.byPriority.map(item => ({
                priority: item._id,
                count: item.count
              }))}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="priority" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </>
    )}

    {/* Feedback Management */}
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Feedback Management</h3>
            <p className="text-sm text-gray-600">Manage user feedback and suggestions</p>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={exportFeedbackToExcel}
              disabled={feedback.length === 0}
              className="px-3 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="h-4 w-4 mr-1 inline" />
              Export Excel
            </button>
            <button
              onClick={() => fetchFeedback(1, pageSize)}
              className="px-3 py-2 text-sm bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              <RefreshCw className="h-4 w-4 mr-1 inline" />
              Refresh
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <select
            value={feedbackFilters.type}
            onChange={(e) => setFeedbackFilters(prev => ({ ...prev, type: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="">All Types</option>
            <option value="bug_report">Bug Report</option>
            <option value="feature_request">Feature Request</option>
            <option value="general_feedback">General Feedback</option>
            <option value="complaint">Complaint</option>
            <option value="suggestion">Suggestion</option>
          </select>

          <select
            value={feedbackFilters.priority}
            onChange={(e) => setFeedbackFilters(prev => ({ ...prev, priority: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="">All Priorities</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>

          <button
            onClick={() => fetchFeedback(1, pageSize)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
          >
            <Filter className="h-4 w-4 mr-1 inline" />
            Apply Filters
          </button>
        </div>
      </div>

      {/* Feedback Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <input
                  type="checkbox"
                  checked={feedback.length > 0 && feedback.every(f => selectedFeedback.includes(f._id))}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedFeedback(prev => [...new Set([...prev, ...feedback.map(f => f._id)])]);
                    } else {
                      setSelectedFeedback(prev => prev.filter(id => !feedback.some(f => f._id === id)));
                    }
                  }}
                  className="h-4 w-4 text-blue-600 rounded border-gray-300"
                />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <div className="flex items-center gap-1">
                  User
                  <button
                    onClick={() => setShowFeedbackPII(v => !v)}
                    className="text-gray-400 hover:text-gray-600 focus:outline-none"
                    title={showFeedbackPII ? 'Hide name & email' : 'Show name & email'}
                  >
                    {showFeedbackPII ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                  </button>
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Priority
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Rating
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Feedback
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {feedback.map((item) => (
              <tr key={item._id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={selectedFeedback.includes(item._id)}
                    onChange={() => handleFeedbackSelection(item._id)}
                    className="h-4 w-4 text-blue-600 rounded border-gray-300"
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                      {showFeedbackPII
                        ? <span className="text-xs font-medium text-gray-700">{item.userName?.charAt(0)?.toUpperCase()}</span>
                        : <User className="h-4 w-4 text-gray-500" />
                      }
                    </div>
                    <div className="ml-3">
                      <div className="text-sm font-medium text-gray-900">
                        {showFeedbackPII ? item.userName : '••••••••'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {showFeedbackPII ? item.userEmail : '••••••••••••'}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                    {item.type?.replace(/_/g, ' ')}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    item.priority === 'critical' ? 'bg-red-100 text-red-800' :
                    item.priority === 'high' ? 'bg-yellow-100 text-yellow-800' :
                    item.priority === 'medium' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {item.priority}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    {renderStars(item.rating)}
                    {item.rating && <span className="ml-1 text-sm text-gray-600">({item.rating})</span>}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div
                    className="max-w-sm cursor-default"
                    onMouseEnter={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      setTooltip({ title: item.title, description: item.description, x: rect.left, y: rect.bottom + 8 });
                    }}
                    onMouseLeave={() => setTooltip(null)}
                  >
                    <div className="text-sm font-medium text-gray-900 line-clamp-1">{item.title}</div>
                    <div className="text-sm text-gray-500 line-clamp-2 whitespace-normal">{item.description}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => openEditModal(item)}
                      className="text-blue-600 hover:text-blue-900"
                      title="Edit Feedback"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => deleteFeedbackAdmin(item._id)}
                      className="text-red-600 hover:text-red-900"
                      title="Delete Feedback"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {feedbackTotal > 0 && (
        <div className="px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-3 text-sm text-gray-600">
            <span>
              Showing {feedbackTotal === 0 ? 0 : (currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, feedbackTotal)} of {feedbackTotal} items
            </span>
            <span className="text-gray-400 hidden sm:inline">|</span>
            <label className="flex items-center gap-1 hidden sm:flex">
              Rows per page:
              <select
                value={pageSize}
                onChange={(e) => { const n = Number(e.target.value); setPageSize(n); fetchFeedback(1, n); }}
                className="ml-1 px-2 py-1 border border-gray-300 rounded text-sm"
              >
                {[5, 10, 25, 50].map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </label>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => fetchFeedback(currentPage - 1, pageSize)}
              disabled={currentPage === 1}
              className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              &lsaquo; Prev
            </button>
            {getFeedbackPageNumbers().map((pg, i) =>
              pg === '...' ? (
                <span key={`ellipsis-${i}`} className="px-2 py-1 text-sm text-gray-400 select-none">…</span>
              ) : (
                <button
                  key={pg}
                  onClick={() => fetchFeedback(pg, pageSize)}
                  className={`px-3 py-1 text-sm border rounded ${
                    currentPage === pg
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'border-gray-300 hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  {pg}
                </button>
              )
            )}
            <button
              onClick={() => fetchFeedback(currentPage + 1, pageSize)}
              disabled={currentPage === totalFeedbackPages}
              className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next &rsaquo;
            </button>
          </div>
          <label className="flex items-center gap-1 text-sm text-gray-600 sm:hidden">
            Rows:
            <select
              value={pageSize}
              onChange={(e) => { const n = Number(e.target.value); setPageSize(n); fetchFeedback(1, n); }}
              className="ml-1 px-2 py-1 border border-gray-300 rounded text-sm"
            >
              {[5, 10, 25, 50].map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>
        </div>
      )}

      {feedback.length === 0 && (
        <div className="text-center py-12">
          <MessageSquare className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No feedback found</h3>
          <p className="mt-1 text-sm text-gray-500">
            No feedback matches your current filters.
          </p>
        </div>
      )}
    </div>

    {/* Most Active Feedback Users */}
    {feedbackAnalytics?.activeUsers?.length > 0 && (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Most Active Feedback Users</h3>
        <div className="space-y-3">
          {feedbackAnalytics.activeUsers.map((user, index) => (
            <div key={user._id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-medium">
                  {index + 1}
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">{user.name}</p>
                  <p className="text-xs text-gray-500">{user.email}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{user.feedbackCount}</p>
                <p className="text-xs text-gray-500">feedback items</p>
                <div className="flex items-center mt-1">
                  {user.avgRating != null && renderStars(Math.round(user.avgRating))}
                  <span className="ml-1 text-xs text-gray-600">
                    ({user.avgRating != null ? user.avgRating.toFixed(1) : 'N/A'})
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )}

  </div>
)}
      </div>
    </div>

    {tooltip && (
      <div
        style={{ position: 'fixed', left: tooltip.x, top: tooltip.y, zIndex: 9999, maxWidth: 320 }}
        className="bg-gray-900 text-white rounded-2xl shadow-2xl px-4 py-3 pointer-events-none"
        onMouseLeave={() => setTooltip(null)}
      >
        <p className="text-xs font-semibold mb-1 text-white">{tooltip.title}</p>
        <p className="text-xs text-gray-300 leading-relaxed">{tooltip.description}</p>
      </div>
    )}

    {confirmDelete.show && (
      <NotificationModal
        variant="error"
        title="Delete Feedback"
        message="Are you sure you want to delete this feedback? This action cannot be undone."
        confirmLabel="Delete"
        onConfirm={confirmDeleteFeedback}
        onClose={() => setConfirmDelete({ show: false, feedbackId: null })}
      />
    )}

    {notification && (
      <NotificationModal
        variant={notification.variant}
        title={notification.title}
        message={notification.message}
        onClose={() => setNotification(null)}
      />
    )}

    {editModal.show && editModal.item && (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Blurred backdrop — click to dismiss */}
        <div
          className="absolute inset-0 bg-gray-900/50 backdrop-blur-sm"
          onClick={closeEditModal}
        />

        {/* Card */}
        <div className="relative w-full max-w-lg bg-white rounded-2xl overflow-hidden shadow-2xl shadow-gray-900/25 ring-1 ring-gray-900/5">

          {/* Top accent bar */}
          <div className="h-[3px] bg-gradient-to-r from-blue-500 via-indigo-500 to-blue-400" />

          {/* Header */}
          <div className="px-6 pt-5 pb-4 flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-lg bg-blue-50 flex-shrink-0">
                  <Edit className="h-3.5 w-3.5 text-blue-600" />
                </span>
                <h2 className="text-[15px] font-semibold text-gray-900 tracking-tight">Edit Feedback</h2>
              </div>
              <p className="text-[11px] text-gray-400 pl-8 truncate">
                <span className="font-medium text-gray-500">{editModal.item.userName}</span>
                {editModal.item.type && (
                  <span> · {editModal.item.type.replace(/_/g, ' ')}</span>
                )}
              </p>
            </div>
            <button
              onClick={closeEditModal}
              className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-all duration-150"
            >
              <XCircle className="h-4 w-4" />
            </button>
          </div>

          {/* Gradient divider */}
          <div className="h-px mx-6 bg-gradient-to-r from-transparent via-gray-200 to-transparent" />

          {/* Fields */}
          <div className="px-6 py-5 space-y-5">
            {/* Title */}
            <div className="space-y-1.5">
              <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-widest">
                Title
              </label>
              <input
                type="text"
                value={editForm.title}
                onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-3.5 py-2.5 text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:bg-white focus:border-blue-400 focus:ring-4 focus:ring-blue-50 transition-all duration-200"
              />
            </div>

            {/* Description */}
            <div className="space-y-1.5">
              <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-widest">
                Description
              </label>
              <textarea
                value={editForm.description}
                onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                rows={6}
                className="w-full px-3.5 py-2.5 text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:bg-white focus:border-blue-400 focus:ring-4 focus:ring-blue-50 transition-all duration-200 resize-none leading-relaxed"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 pb-5 pt-1 flex items-center justify-between gap-4">
            <p className="text-[11px] text-gray-400 leading-snug hidden sm:block">
              Edits are reflected immediately on the user's end.
            </p>
            <div className="flex items-center gap-2 ml-auto">
              <button
                onClick={closeEditModal}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-all duration-150"
              >
                Cancel
              </button>
              <button
                onClick={saveEditModal}
                className="px-5 py-2 text-sm font-semibold text-white bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl shadow-sm shadow-blue-200 hover:shadow-md hover:shadow-blue-300 hover:from-blue-500 hover:to-indigo-500 active:scale-[0.97] transition-all duration-150"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default AdminDashboard;